import { JobType } from '../redux/jobs/jobs.types';
import { splitJoin } from '../helper/TextHelper';
import { FieldVisibility } from './fieldVisibility';

export interface JobRowCandidateInput {
    job: JobType;
    visibility: FieldVisibility;
    /** Aus der Elternliste, nicht je Zeile berechnet — siehe JobRowList. */
    hasObjectKeyMultipleValues: boolean;
    hasLocationDescriptionMultipleValues: boolean;
    /** Schon übersetzt, weil dieses Modul kein i18n kennt. */
    meterTypeLabel: string;
}

/**
 * Die Kennungen einer Auftragszeile, in fachlicher Wichtigkeit. Der Aufrufer
 * belegt damit seine Plätze von oben: JobListItem hat drei (Titel, Zeile 1,
 * Zeile 2), JobListShortItem einen.
 *
 * Ist nichts versteckt UND die Adresse gesetzt, ergibt die Liste genau die
 * Belegung von heute: Titel die Zählernummer, Zeile 1 die Adresse, Zeile 2 der
 * erste verfügbare aus Objektschlüssel / Beschreibung / Zählerart. Die alte
 * Rückfallkette in `secondLine` WAR bereits dieser Mechanismus, nur auf einen
 * Platz beschränkt — hier gilt er für alle drei. Versteckt man die
 * Zählernummer, rückt die Adresse in den Titel; niemand muss eine zweite Regel
 * lernen.
 *
 * EINE bewusste Abweichung: heute sind die drei Plätze fest verdrahtet, ein
 * leerer Wert lässt seinen Platz leer. Hier rücken die Kandidaten auf. Bei
 * einem Auftrag OHNE Adresse steht der Objektschlüssel deshalb künftig auf
 * Zeile 1 statt Zeile 2, und die Zählerart füllt den frei gewordenen Platz.
 * Das ist die bessere Ausgabe — kein leerer Platz, während ein späterer
 * Kandidat etwas zu sagen hätte — aber es ist eine Änderung, kein Erhalt.
 * Festgehalten in jobRowCandidates.test.ts.
 *
 * Leere Werte fallen heraus, damit ein Platz nicht leer bleibt, während ein
 * späterer Kandidat etwas zu sagen hätte.
 */
export function jobRowCandidates({
    job,
    visibility,
    hasObjectKeyMultipleValues,
    hasLocationDescriptionMultipleValues,
    meterTypeLabel,
}: JobRowCandidateInput): string[] {
    const address = job.meter.meterAddress;

    // null kommt von splitJoin, false von den Automatik-Flaggen. Der Filter
    // unten laesst nur nicht-leere Strings durch.
    const candidates: (string | undefined | false | null)[] = [
        visibility.pick('meter.meterNumber', job.meter.meterNumber),

        splitJoin(
            ', ',
            visibility.pick('meter.meterAddress.city', address?.city),
            splitJoin(
                ' ',
                visibility.pick('meter.meterAddress.street', address?.street),
                visibility.pick('meter.meterAddress.houseNumber', address?.houseNumber),
            ),
        ),

        // Die Automatik zählt mit: eine Angabe, die in jeder Zeile gleich
        // lautet, taugt nicht als Kennung.
        hasObjectKeyMultipleValues && visibility.pick('meter.meterAddress.objectKey', address?.objectKey),

        hasLocationDescriptionMultipleValues &&
            visibility.pick('meter.meterAddress.locationDescription', address?.locationDescription),

        visibility.pick('meter.meterType', job.meter.customMeterType || meterTypeLabel),
    ];

    return candidates.filter((value): value is string => typeof value === 'string' && value.length > 0);
}
